import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiArrowLongRight, HiArrowUpRight } from "react-icons/hi2";
import {
  formatNewsDate,
  getExternalLink,
  getNewsExcerpt,
  getNewsImage,
  getVideoEmbedUrl,
  getYoutubeId,
  isNewsFallbackImage,
  onNewsImageError,
} from "../../../http/useNews";

// Article image, marked `is-fallback` when the branded placeholder is shown instead.
export const NewsImage = ({ article }) => {
  const src = getNewsImage(article);
  return (
    <img
      src={src}
      alt={article.name}
      className={isNewsFallbackImage(src) ? "is-fallback" : undefined}
      onError={onNewsImageError}
    />
  );
};

// Opens the article's external `path` in a new tab, or its news page otherwise.
export const NewsLink = ({ article, className, children }) => {
  const external = getExternalLink(article);
  return external ? (
    <a href={external} className={className} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ) : (
    <Link to={`/news/${article.id}`} className={className}>
      {children}
    </Link>
  );
};

export const NewsVideo = ({ article, className }) => (
  <div className={className}>
    <iframe
      src={getVideoEmbedUrl(article.path)}
      title={article.name}
      loading="lazy"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  </div>
);

export const DateBadge = ({ date, lang }) => {
  const locale = lang === "ar" ? "ar-JO" : "en-GB";
  const d = new Date(date);
  return (
    <span className="HomeNews-badge">
      <strong>{d.toLocaleDateString(locale, { day: "numeric" })}</strong>
      {d.toLocaleDateString(locale, { month: "short" })}
    </span>
  );
};

// Large story card; `solo` puts the media beside the text when it is the only article.
// YouTube articles show the playable video in place of the cover image.
export const NewsFeatured = ({ article, solo }) => {
  const { t, i18n } = useTranslation();
  const ArrowIcon = getExternalLink(article) ? HiArrowUpRight : HiArrowLongRight;
  const isVideo = Boolean(getYoutubeId(article.path));

  const body = (
    <>
      {isVideo && (
        <span className="NewsCard-date">{formatNewsDate(article.date, i18n.language)}</span>
      )}
      <h3>{article.name}</h3>
      <p>{getNewsExcerpt(article)}</p>
      <span className="HomeNews-more">
        {t("Read-More")}
        <ArrowIcon className="HomeNews-arrow" />
      </span>
    </>
  );

  if (isVideo) {
    return (
      <div className={`HomeNews-featured video${solo ? " solo" : ""}`}>
        <NewsVideo article={article} className="HomeNews-featured-video" />
        <NewsLink article={article} className="HomeNews-featured-body">
          {body}
        </NewsLink>
      </div>
    );
  }

  return (
    <NewsLink article={article} className={`HomeNews-featured${solo ? " solo" : ""}`}>
      <NewsImage article={article} />
      {getVideoEmbedUrl(article.path) && <span className="NewsCard-play" />}
      <DateBadge date={article.date} lang={i18n.language} />
      <div className="HomeNews-featured-body">{body}</div>
    </NewsLink>
  );
};

export const NewsCard = ({ article }) => {
  const { t, i18n } = useTranslation();
  const excerpt = getNewsExcerpt(article, 110);
  const ArrowIcon = getExternalLink(article) ? HiArrowUpRight : HiArrowLongRight;
  const isVideo = Boolean(getYoutubeId(article.path));

  const body = (
    <>
      {isVideo && (
        <span className="NewsCard-date">{formatNewsDate(article.date, i18n.language)}</span>
      )}
      <h3>{article.name}</h3>
      {excerpt && <p>{excerpt}</p>}
      <div className="NewsCard-footer">
        <span>{t("Read-More")}</span>
        <span className="NewsCard-arrow">
          <ArrowIcon className="HomeNews-arrow" />
        </span>
      </div>
    </>
  );

  if (isVideo) {
    return (
      <div className="NewsCard video">
        <NewsVideo article={article} className="NewsCard-image" />
        <NewsLink article={article} className="NewsCard-body">
          {body}
        </NewsLink>
      </div>
    );
  }

  return (
    <NewsLink article={article} className="NewsCard">
      <div className="NewsCard-image">
        <NewsImage article={article} />
        {getVideoEmbedUrl(article.path) && <span className="NewsCard-play sm" />}
        <DateBadge date={article.date} lang={i18n.language} />
      </div>
      <div className="NewsCard-body">{body}</div>
    </NewsLink>
  );
};
